import React, { useState, useCallback, useRef, useEffect } from "react";
import { Table, Tag } from "antd";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import update from "immutability-helper";
import { Offcanvas } from "react-bootstrap";
import useApiServices from "../../utils/useApiServices";
import {
  UPDATE_MID_RULE2S,
  GET_AMOUNT_RULES_MID,
  DISABLE_RULE_FRM,
} from "../../utils/constant";
import swal from "sweetalert";
import DragDropRules from "./DragDropRules";
import ShuffleRules from "./ShuffleRules";

const FRMShuffleRule = ({
  showRule,
  setShowRule,
  mid,
  Id,
  viewOnly,
  type,
  getAllMerchantsWithTheirRules,
}) => {
  const [data, setData] = useState([]);
  const [viewSid, setViewSid] = useState(false);
  const [editedRowValues, setEditedRowValues] = useState({});

  const [id, setId] = useState(null);
  const [sidData, setSidData] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [editedData, setEditedData] = useState([]);

  const {
    apiCallFnforGet,
    apiCallFnforPost,
    apiCallFnforPut,
    apiCallFnforDelete,
  } = useApiServices();

  const FetchDataForRules = async () => {
    try {
      const response = await apiCallFnforGet(GET_AMOUNT_RULES_MID + mid);
      if (response?.statusCode === 200) {
        console.log(response?.data);
        setData(response?.data);
      } else {
        setData(response?.data);
      }
    } catch (error) {
      console.error(error?.message);
    }
  };

  useEffect(() => {
    FetchDataForRules(type);
  }, [showRule]);

  const submitUpdatedRules = async (row) => {
    const hasRow = !!row?.id;

    // Get current data (preserving order after drag)
    const baseData = hasRow ? [data.find((item) => item.id === row.id)] : data;

    const payload = baseData.map((rowItem, index) => {
      const edited = editedRowValues[rowItem.id] || {};
      return {
        id: rowItem.id,
        txnMinAmount: Number(edited.txnMinAmount ?? rowItem.txnMinAmount),
        txnMaxAmount: Number(edited.txnMaxAmount ?? rowItem.txnMaxAmount),
        // priority: index + 1, // if backend needs new order
        // ...add any other fields from rowItem if needed
      };
    });

    const confirm = await swal({
      title: hasRow ? "Update Row?" : "Submit All Rows?",
      text: "Are you sure you want to proceed?",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (!confirm) return;

    // console.log(payload);
    // return;

    try {
      const response = await apiCallFnforPut(UPDATE_MID_RULE2S, payload);
      if (response.statusCode === 200) {
        swal("Success", "Rule Updated Successfully", "success");
        setEditedRowValues((prev) => {
          if (hasRow) {
            const { [row.id]: _, ...rest } = prev;
            return rest;
          }
          return {};
        });
        FetchDataForRules();
        setShowRule(false);
      } else {
        swal("Error", response.message, "error");
      }
    } catch (err) {
      swal("Error", err.message || "Something went wrong", "error");
    }
  };

  const DraggableBodyRow = ({
    index,
    moveRow,
    className,
    style,
    ...restProps
  }) => {
    const ref = useRef();
    const [{ isOver, dropClassName }, drop] = useDrop({
      accept: "DraggableBodyRow",
      collect: (monitor) => {
        const { index: dragIndex } = monitor.getItem() || {};
        if (dragIndex === index) {
          return {};
        }
        return {
          isOver: monitor.isOver(),
          dropClassName:
            dragIndex < index ? " drop-over-downward" : " drop-over-upward",
        };
      },
      drop: (item) => {
        moveRow(item.index, index);
      },
    });

    const [, drag] = useDrag({
      type: "DraggableBodyRow",
      item: { index },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    });

    drop(drag(ref));

    return (
      <tr
        ref={ref}
        className={`${className}${isOver ? dropClassName : ""}`}
        style={{ cursor: "move", ...style }}
        {...restProps}
      ></tr>
    );
  };

  const handleSidView = async (id) => {
    // alert(id);
    setViewSid(true);
    setId(id);
  };

  const columns = [
    {
      title: "MID",
      dataIndex: "mid",
      key: "mid",
    },
    {
      title: "Vehicle Name",
      dataIndex: "vehicleName",
      key: "vehicleName",
    },
    // {
    //   title: "Domain",
    //   dataIndex: "domain",
    //   key: "domain",
    // },
    // {
    //   title: "Processor",
    //   dataIndex: "processor",
    //   key: "processor",
    // },

    {
      title: "Request Mode",
      dataIndex: "requestMode",
      key: "requestMode",
    },
    {
      title: "Denied/Route",
      dataIndex: "isDenied",
      key: "isDenied",
      render: (isDenied) => (
        <Tag color={!isDenied ? "green" : "red"}>
          {!isDenied ? "Route" : "Denied"}
        </Tag>
      ),
    },
    {
      title: "Txn Min Amount",
      dataIndex: "txnMinAmount",
      key: "txnMinAmount",
      render: (value, record) =>
        !viewOnly ? (
          <input
            type="number"
            className="form-control form-control-sm"
            value={
              editedRowValues[record.id]?.txnMinAmount ??
              record.txnMinAmount ??
              ""
            }
            onChange={(e) => {
              const newVal = e.target.value;
              setEditedRowValues((prev) => ({
                ...prev,
                [record.id]: {
                  ...(prev[record.id] || record), // Merge with original row
                  txnMinAmount: newVal,
                },
              }));
            }}
          />
        ) : (
          record?.txnMinAmount
        ),
    },
    {
      title: "Txn Max Amount",
      dataIndex: "txnMaxAmount",
      key: "txnMaxAmount",
      render: (value, record) =>
        !viewOnly ? (
          <input
            type="number"
            className="form-control form-control-sm"
            value={
              editedRowValues[record.id]?.txnMaxAmount ??
              record?.txnMaxAmount ??
              ""
            }
            onChange={(e) => {
              const newVal = e.target.value;
              setEditedRowValues((prev) => ({
                ...prev,
                [record.id]: {
                  ...(prev[record.id] || record), // Merge with original row
                  txnMaxAmount: newVal,
                },
              }));
            }}
          />
        ) : (
          record?.txnMaxAmount
        ),
    },
    {
      title: "Action",
      dataIndex: "id",
      key: "id",
      render: (id, record) =>
        !viewOnly && (
          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-primary btn-sm"
              onClick={() => submitUpdatedRules()}
            >
              Update Amount Range
            </button>
            <button
              className="btn btn-danger btn-sm"
              onClick={() => handleRuleDelete(id)}
            >
              Delete
            </button>
          </div>
        ),
    },

    {
      title: "View Sid",
      dataIndex: "id",
      key: "id",
      render: (id) => (
        <button
          className="btn btn-info btn-sm"
          onClick={() => handleSidView(id)}
        >
          View SID's
        </button>
      ),
    },
  ];

  const handleRuleDelete = async (id) => {
    try {
      swal({
        title: "Alert",
        text: `Are you sure, you want to Delete this rule?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      }).then(async (willDisable) => {
        if (willDisable) {
          const response = await apiCallFnforDelete(DISABLE_RULE_FRM + id);

          if (response.statusCode === 200) {
            swal({
              title: "Success",
              text: response.message,
              icon: "success",
            });
            FetchDataForRules(type);
          } else {
            swal({
              title: "Alert",
              text: response.message,
              icon: "error",
            });
          }
        } else {
        }
      });
    } catch (error) {
      swal({
        title: "Alert",
        text: error,
        icon: "error",
      });
    }
  };

  const offcanvasStyle = {
    width: "90vw", // Set width to 70vw
  };

  const [show, setShow] = useState(true);

  const handleClose = () => {
    setShowRule(false);
  };

  const moveRow = useCallback(
    (dragIndex, hoverIndex) => {
      const dragRow = data[dragIndex];
      const newData = update(data, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, dragRow],
        ],
      });

      const updatedData = newData.map((item, index) => ({
        ...item,
      }));

      setData(updatedData);
    },
    [data]
  );

  const components = {
    body: {
      row: (props) => <DraggableBodyRow {...props} moveRow={moveRow} />,
    },
  };

  return (
    <>
      <Offcanvas
        show={showRule}
        onHide={handleClose}
        placement="end"
        style={offcanvasStyle}
        backdrop="static"
        keyboard={false}
      >
        <div className="d-flex justify-content-start align-items-center">
          <Offcanvas.Header closeButton></Offcanvas.Header>
          <h5
            class="offcanvas-title  fs-2 text-dark fw-bold"
            id="offcanvasLabel"
          >
            {type} Routing Rule for {mid}
          </h5>
        </div>
        <Offcanvas.Body>
          <div className="overflow-x-scroll">
            <DndProvider backend={HTML5Backend}>
              <Table
                columns={columns}
                dataSource={data}
                components={components}
                pagination={false}
                onRow={(record, index) => ({
                  index,
                  moveRow,
                })}
                rowKey="prority"
                scroll={{ x: "max-content" }}
              />
            </DndProvider>
            {!viewOnly && (
              <div className="mt-3">
                <button
                  className="btn btn-primary px-4 py-2"
                  onClick={submitUpdatedRules}
                >
                  Submit
                </button>
              </div>
            )}
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      {viewSid && id && (
        <DragDropRules
          id={id}
          mid={mid}
          getAllMerchantsWithTheirRules={getAllMerchantsWithTheirRules}
          showRule={viewSid}
          setShowRule={setViewSid}
          viewOnly={viewOnly}
          type={"FRM"}
        />
      )}
    </>
  );
};

export default FRMShuffleRule;
